import { ReactNode, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import Section from 'components/common/Section/Section';
import PencilIcon from 'icons/PencilIcon';
import SaveIcon from 'icons/SaveIcon';
import XIcon from 'icons/XIcon';
import { useUpdateStore } from 'hooks/useStore';
import { AdminUpdateStoreDto } from 'services/storeService';
import { snackbarService } from 'services/snackbarService';
import { colors } from 'styles/colors';
import { SectionButtonType } from 'types/types';

interface Props<T extends Record<string, any>> {
  title: string;
  storeId: string;
  // What the section shows while it is not being edited.
  view: ReactNode;
  // The inputs, rendered inside this section's form.
  form: ReactNode;
  // The form's values as the store holds them now.
  values: T;
  // The form's values turned into the one part of the update this section owns.
  toPayload: (values: T) => AdminUpdateStoreDto;
  onSaved: () => void;
  buttons?: SectionButtonType[];
}

// One section of a store's details that can be switched into a form and saved
// on its own, the way the product page's sections are.
export default function StoreEditableSection<T extends Record<string, any>>({
  title,
  storeId,
  view,
  form,
  values,
  toPayload,
  onSaved,
  buttons = [],
}: Props<T>) {
  const [isEditing, setIsEditing] = useState(false);
  const methods = useForm<T>({ defaultValues: values as any });

  const { mutate: updateStore, isLoading } = useUpdateStore(
    () => {
      setIsEditing(false);
      snackbarService.showSnackbar(`${title} has been updated`, 'success');
      onSaved();
    },
    (error: any) => {
      const message = error?.response?.data?.message;
      snackbarService.showSnackbar(
        `Could not update ${title.toLowerCase()}${message ? ` - ${Array.isArray(message) ? message.join('; ') : message}` : ''}`,
        'error',
      );
    },
  );

  const onSubmit = (formValues: T) => {
    updateStore({ storeId, data: toPayload(formValues) });
  };

  return (
    <FormProvider {...methods}>
      <Section
        title={title}
        buttons={isEditing
          ? [
            {
              icon: <SaveIcon size={20} color={colors.success} />,
              title: 'Save',
              onClick: methods.handleSubmit(onSubmit as any),
              loading: isLoading,
            },
            {
              icon: <XIcon size={15} color={colors.error} />,
              title: 'Cancel',
              onClick: () => setIsEditing(false),
              disabled: isLoading,
            },
          ]
          : [
            ...buttons,
            {
              icon: <PencilIcon size={20} color={colors.primary} />,
              title: 'Edit',
              // Reset before the form mounts, so it opens on what the store holds
              // now rather than first showing stale values and then changing them.
              onClick: () => {
                methods.reset(values as any);
                setIsEditing(true);
              },
            },
          ]}
        content={isEditing ? form : view}
      />
    </FormProvider>
  );
}
